import { useLiveSessions } from "@/hooks/call-center/useLiveSessions";
import { useWaitingRoomLeads } from "@/hooks/call-center/useWaitingRoomLeads";
import { useOtherAgentEngagements } from "@/hooks/call-center/useOtherAgentEngagements";
import useAuthUser from "@/hooks/useAuthUser";
import { useCallCentersStore } from "@/store/call-centers/call-centers.store";
import { useSocketContext } from "@/context/socket.context/socket.context";
import { TJoinedClient, TLiveAgent, TLiveSession } from "@/models/call.center.model";
import { getMyCurrentCallCenterStatus, notifyAgentStatusChanged } from "@/services/call-center/agents.service";
import { CALLCENTER_USER_STATUS_IN_CALL, CALLCENTER_USER_STATUS_READY, CUSTOM_EVENT_USER_STATUS_CHANGED } from "@icebergeradmin/common";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { setAgentAvailable } from "@/lib/agentAvailability";

type CallCenterProps = {
  liveCalls: TLiveSession[];
  joinedLeads: TJoinedClient[];
  agentStatus?: number;
  isAgentAvailable: boolean;
  personalRoomSessionName: string;
  /** sessionName -> name of another agent currently calling that lead (voice). */
  engagedByOtherAgent: Record<string, string>;
  updateAgentStatus: (newStatus?: number) => void;
  removeWaitingRoomEntry: (entryId: string) => void;
};


const CallCenterContext = createContext<CallCenterProps>({
  liveCalls: [],
  joinedLeads: [],
  isAgentAvailable: false,
  personalRoomSessionName: "",
  engagedByOtherAgent: {},
  updateAgentStatus: () => undefined,
  removeWaitingRoomEntry: () => undefined,
});

const CallCenterProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuthUser();
  const socket = useSocketContext();
  const selectionReady = useCallCentersStore((s) => s.selectionReady);
  const selectedCallCenter = useCallCentersStore((s) => s.selectedCallCenter);
  const { joinedLeads, liveCalls } = useLiveSessions(selectionReady ? selectedCallCenter?.id : undefined);
  const [agentStatus, setAgentStatus] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  // "Agent" = someone who is assigned to at least one call center
  const isAgent = !!user.assignedCallCenterIds?.length

  const personalRoomSessionName =
    user?.companyUniqueName && user?.personalRoomId
      ? `${user.companyUniqueName}-${user.personalRoomId}`
      : "";

  const { waitingRoomLeads, removeEntry: removeWaitingRoomEntry } = useWaitingRoomLeads(personalRoomSessionName);
  const engagedByOtherAgent = useOtherAgentEngagements();

  const allJoinedLeads = useMemo(
    () => [...joinedLeads, ...waitingRoomLeads],
    [joinedLeads, waitingRoomLeads]
  );


  useEffect(() => {
    if (!isAgent) return setIsLoading(false);
    let mounted = true;
    getMyCurrentCallCenterStatus().then((status) => {
      if (mounted) setAgentStatus(status);
      setIsLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [isAgent]);

  const updateAgentStatus = useCallback((newStatus?: number) => {
    if (newStatus === undefined) return;
    setAgentStatus((prev) => {
      if (prev === newStatus) return prev;
      notifyAgentStatusChanged(newStatus);
      return newStatus;
    });
  }, []);

  // ── Single source of truth across tabs/devices ───────────────────────
  // The backend broadcasts CUSTOM_EVENT_USER_STATUS_CHANGED to every socket
  // in the call center whenever an agent's status changes. We reconcile our
  // OWN status from that event so a change made in one tab (or another
  // device) is reflected everywhere — not just in the avatar list. This is
  // also why we keep the optimistic update in `updateAgentStatus`: the echo
  // for our own change just re-affirms the same value (React bails out).
  useEffect(() => {
    if (!isAgent || !user.id) return;
    const onStatusChanged = (agent: TLiveAgent) => {
      if (agent.id !== user.id || typeof agent.status !== "number") return;
      setAgentStatus(agent.status);
    };
    socket.on(CUSTOM_EVENT_USER_STATUS_CHANGED, onStatusChanged);
    return () => {
      socket.off(CUSTOM_EVENT_USER_STATUS_CHANGED, onStatusChanged);
    };
  }, [socket, isAgent, user.id]);

  const isAgentAvailable = agentStatus === CALLCENTER_USER_STATUS_READY || agentStatus === CALLCENTER_USER_STATUS_IN_CALL;

  useEffect(() => {
    if (!isLoading) setAgentAvailable(isAgentAvailable);
  }, [isAgentAvailable, isLoading]);

  if (isLoading) return <div>Loading</div>;
  return (
    <CallCenterContext.Provider value={{ liveCalls, joinedLeads: allJoinedLeads, agentStatus, isAgentAvailable, personalRoomSessionName, engagedByOtherAgent, updateAgentStatus, removeWaitingRoomEntry }}>
      {children}
    </CallCenterContext.Provider>
  );
};

export default CallCenterProvider;

export const useCallCenterContext = () => useContext(CallCenterContext);
